import React, { useCallback, useEffect, useState } from "react";
import { Save, RotateCcw, Plus, Trash2, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import ImageUploadField from "../components/ImageUploadField";

export default function PageContentAdmin() {
  const [content, setContent] = useState(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [openSection, setOpenSection] = useState("hero");

  const load = useCallback(() => {
    setStatus({ state: "loading", msg: "" });
    api
      .get("/home-content")
      .then((r) => {
        setContent(r.data);
        setStatus({ state: "idle", msg: "" });
      })
      .catch(() => setStatus({ state: "error", msg: "Could not load content" }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setStatus({ state: "saving", msg: "" });
    try {
      await api.put("/admin/home-content", content);
      setStatus({ state: "success", msg: "Saved. Reload the homepage to see changes." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Save failed",
      });
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset ALL homepage content to defaults? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/home-content/reset");
      setContent(data);
      setStatus({ state: "success", msg: "Reset to defaults." });
    } catch {
      setStatus({ state: "error", msg: "Reset failed" });
    }
  };

  const updateSection = (section, updates) => {
    setContent((prev) => ({ ...prev, [section]: { ...prev[section], ...updates } }));
  };

  const updateSlide = (idx, updates) => {
    setContent((prev) => {
      const slides = [...prev.hero.slides];
      slides[idx] = { ...slides[idx], ...updates };
      return { ...prev, hero: { ...prev.hero, slides } };
    });
  };

  const addSlide = () => {
    setContent((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        slides: [
          ...prev.hero.slides,
          {
            overline: "New Slide",
            title_top: "Headline",
            title_italic: "Accent",
            sub: "Subtitle copy.",
            cta_label: "Learn More",
            cta_url: "/about",
            image_url: "",
          },
        ],
      },
    }));
  };

  const removeSlide = (idx) => {
    if (!window.confirm("Remove this hero slide?")) return;
    setContent((prev) => ({
      ...prev,
      hero: { ...prev.hero, slides: prev.hero.slides.filter((_, i) => i !== idx) },
    }));
  };

  const updateParagraph = (idx, value) => {
    setContent((prev) => {
      const paragraphs = [...prev.letter.paragraphs];
      paragraphs[idx] = value;
      return { ...prev, letter: { ...prev.letter, paragraphs } };
    });
  };

  const addParagraph = () =>
    setContent((prev) => ({
      ...prev,
      letter: { ...prev.letter, paragraphs: [...prev.letter.paragraphs, ""] },
    }));

  const removeParagraph = (idx) => {
    if (!window.confirm("Remove this paragraph?")) return;
    setContent((prev) => ({
      ...prev,
      letter: {
        ...prev.letter,
        paragraphs: prev.letter.paragraphs.filter((_, i) => i !== idx),
      },
    }));
  };

  if (!content) {
    return (
      <p className="font-overline text-xs text-stone-500">
        {status.state === "error" ? status.msg : "Loading content…"}
      </p>
    );
  }

  return (
    <div data-testid="page-content-admin" className="pb-32">
      {/* Top heading row (non-sticky) */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-10 pb-6 border-b border-white/10">
        <div>
          <h3 className="font-display text-3xl tracking-tight">Home Page Content</h3>
          <p className="font-overline text-[0.65rem] text-stone-400 mt-2 max-w-md">
            Edit every section of the homepage. Changes save together — don't forget to click Save Changes.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            data-testid="page-content-reset"
            onClick={resetDefaults}
            className="btn-outline !py-2 !px-4 !text-[0.65rem]"
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            data-testid="page-content-save"
            onClick={save}
            disabled={status.state === "saving"}
            className="btn-gold !py-2 !px-5 !text-[0.7rem] disabled:opacity-50"
          >
            <Save size={12} /> {status.state === "saving" ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {status.state === "success" && (
        <p data-testid="page-content-success" className="mb-6 font-overline text-xs text-[#DE9E48]">
          ✓ {status.msg}
        </p>
      )}
      {status.state === "error" && (
        <p data-testid="page-content-error" className="mb-6 font-overline text-xs text-red-400">
          ✕ {status.msg}
        </p>
      )}

      <div className="space-y-4">
        {/* HERO SLIDES */}
        <Accordion
          id="hero"
          label="Hero Slides"
          summary={`${content.hero.slides.length} slide${content.hero.slides.length === 1 ? "" : "s"}`}
          open={openSection === "hero"}
          onToggle={() => setOpenSection(openSection === "hero" ? "" : "hero")}
        >
          <div className="space-y-6">
            {content.hero.slides.map((s, i) => (
              <div key={i} className="border border-white/10 bg-stone-950/40 p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-overline text-[0.65rem] text-[#DE9E48]">Slide {i + 1}</p>
                  <button
                    data-testid={`slide-remove-${i}`}
                    onClick={() => removeSlide(i)}
                    className="text-stone-400 hover:text-red-400"
                    aria-label="Remove slide"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field
                    label="Overline (small label)"
                    value={s.overline}
                    onChange={(v) => updateSlide(i, { overline: v })}
                  />
                  <ImageUploadField
                    label="Background image"
                    testid={`slide-image-${i}`}
                    value={s.image_url}
                    onChange={(v) => updateSlide(i, { image_url: v })}
                  />
                  <Field
                    label="Title — first line"
                    value={s.title_top}
                    onChange={(v) => updateSlide(i, { title_top: v })}
                  />
                  <Field
                    label="Title — italic accent line"
                    value={s.title_italic}
                    onChange={(v) => updateSlide(i, { title_italic: v })}
                  />
                  <TextArea
                    label="Subtitle"
                    value={s.sub}
                    onChange={(v) => updateSlide(i, { sub: v })}
                    rows={2}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Button label"
                      value={s.cta_label}
                      onChange={(v) => updateSlide(i, { cta_label: v })}
                    />
                    <Field
                      label="Button link (/path or https://)"
                      value={s.cta_url}
                      onChange={(v) => updateSlide(i, { cta_url: v })}
                    />
                  </div>
                </div>
                {s.image_url && (
                  <img
                    src={s.image_url}
                    alt=""
                    className="mt-4 w-full max-h-32 object-cover border border-white/10"
                  />
                )}
              </div>
            ))}
            <button
              data-testid="slide-add"
              onClick={addSlide}
              className="btn-outline !py-2 !px-4 !text-[0.65rem]"
            >
              <Plus size={12} /> Add Slide
            </button>
          </div>
        </Accordion>

        {/* QUOTE */}
        <Accordion
          id="quote"
          label="Paul Robeson Quote Section"
          open={openSection === "quote"}
          onToggle={() => setOpenSection(openSection === "quote" ? "" : "quote")}
        >
          <div className="space-y-5">
            <TextArea
              label="Quote text"
              value={content.quote.text}
              onChange={(v) => updateSection("quote", { text: v })}
              rows={3}
            />
            <Field
              label="Attribution"
              value={content.quote.attribution}
              onChange={(v) => updateSection("quote", { attribution: v })}
            />
            <TextArea
              label="Body paragraph below the quote"
              value={content.quote.body}
              onChange={(v) => updateSection("quote", { body: v })}
              rows={4}
            />
          </div>
        </Accordion>

        {/* LETTER */}
        <Accordion
          id="letter"
          label="Letter from the Artistic Director"
          summary={`${content.letter.paragraphs.length} paragraph${
            content.letter.paragraphs.length === 1 ? "" : "s"
          }`}
          open={openSection === "letter"}
          onToggle={() => setOpenSection(openSection === "letter" ? "" : "letter")}
        >
          <div className="space-y-5">
            <Field
              label="Section overline"
              value={content.letter.overline}
              onChange={(v) => updateSection("letter", { overline: v })}
            />
            <div className="grid md:grid-cols-3 gap-4">
              <Field
                label="Headline — first line"
                value={content.letter.title_top}
                onChange={(v) => updateSection("letter", { title_top: v })}
              />
              <Field
                label="Headline — italic word"
                value={content.letter.title_italic}
                onChange={(v) => updateSection("letter", { title_italic: v })}
              />
              <Field
                label="Headline — text after italic"
                value={content.letter.title_after}
                onChange={(v) => updateSection("letter", { title_after: v })}
              />
            </div>
            <Field
              label="Greeting (handwritten script)"
              value={content.letter.greeting}
              onChange={(v) => updateSection("letter", { greeting: v })}
            />
            <ImageUploadField
              label="Director image"
              testid="letter-director-image"
              value={content.letter.director_image_url}
              onChange={(v) => updateSection("letter", { director_image_url: v })}
            />
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Director name"
                value={content.letter.director_name}
                onChange={(v) => updateSection("letter", { director_name: v })}
              />
              <Field
                label="Director title"
                value={content.letter.director_title}
                onChange={(v) => updateSection("letter", { director_title: v })}
              />
            </div>

            <div className="border-t border-white/10 pt-5">
              <p className="font-overline text-[0.65rem] text-[#DE9E48] mb-3">
                Letter paragraphs (the pulled quote appears after paragraph 2)
              </p>
              <div className="space-y-3">
                {content.letter.paragraphs.map((p, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="font-overline text-[0.65rem] text-stone-500 pt-3 w-6">
                      {i + 1}.
                    </span>
                    <textarea
                      data-testid={`paragraph-${i}`}
                      value={p}
                      onChange={(e) => updateParagraph(i, e.target.value)}
                      rows={4}
                      className="field-textarea flex-1"
                    />
                    <button
                      data-testid={`paragraph-remove-${i}`}
                      onClick={() => removeParagraph(i)}
                      className="text-stone-400 hover:text-red-400 mt-3"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  data-testid="paragraph-add"
                  onClick={addParagraph}
                  className="btn-outline !py-2 !px-4 !text-[0.65rem]"
                >
                  <Plus size={12} /> Add Paragraph
                </button>
              </div>
            </div>

            <TextArea
              label="Pulled quote (between paragraphs 2 & 3)"
              value={content.letter.pulled_quote}
              onChange={(v) => updateSection("letter", { pulled_quote: v })}
              rows={2}
            />
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Signoff (e.g. Yours in story,)"
                value={content.letter.signoff}
                onChange={(v) => updateSection("letter", { signoff: v })}
              />
              <Field
                label="Signature name (handwritten)"
                value={content.letter.signature_name}
                onChange={(v) => updateSection("letter", { signature_name: v })}
              />
            </div>
          </div>
        </Accordion>

        {/* SNAPSHOTS HEADING */}
        <Accordion
          id="snapshots"
          label="Snapshots Section Heading"
          open={openSection === "snapshots"}
          onToggle={() => setOpenSection(openSection === "snapshots" ? "" : "snapshots")}
        >
          <HeadingFields section="snapshots" data={content.snapshots} update={updateSection} />
        </Accordion>

        {/* ADDRESS BAND */}
        <Accordion
          id="address_band"
          label="Address Band"
          open={openSection === "address_band"}
          onToggle={() => setOpenSection(openSection === "address_band" ? "" : "address_band")}
        >
          <div className="space-y-5">
            <Field
              label="Overline"
              value={content.address_band.overline}
              onChange={(v) => updateSection("address_band", { overline: v })}
            />
            <Field
              label="Street address (big)"
              value={content.address_band.title}
              onChange={(v) => updateSection("address_band", { title: v })}
            />
            <Field
              label="City / state / ZIP"
              value={content.address_band.subtitle}
              onChange={(v) => updateSection("address_band", { subtitle: v })}
            />
          </div>
        </Accordion>

        {/* ARTICLES HEADING */}
        <Accordion
          id="articles"
          label="Latest Articles Heading"
          open={openSection === "articles"}
          onToggle={() => setOpenSection(openSection === "articles" ? "" : "articles")}
        >
          <HeadingFields section="articles" data={content.articles} update={updateSection} />
        </Accordion>
      </div>

      {/* Bottom save bar — visible after scrolling through long forms */}
      <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
        <p className="font-overline text-[0.65rem] text-stone-500">
          Click Save Changes to publish edits to the live homepage.
        </p>
        <div className="flex gap-2">
          <button
            data-testid="page-content-reset-bottom"
            onClick={resetDefaults}
            className="btn-outline !py-2 !px-4 !text-[0.65rem]"
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            data-testid="page-content-save-bottom"
            onClick={save}
            disabled={status.state === "saving"}
            className="btn-gold !py-2 !px-5 !text-[0.7rem] disabled:opacity-50"
          >
            <Save size={12} /> {status.state === "saving" ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------- helpers --------
function Accordion({ id, label, summary, open, onToggle, children }) {
  return (
    <section className="border border-white/10 bg-stone-900/30">
      <button
        data-testid={`accordion-${id}`}
        onClick={onToggle}
        className="w-full p-5 flex items-center justify-between text-left hover:bg-stone-900/50 transition-colors"
      >
        <div>
          <h4 className="font-display text-xl tracking-tight">{label}</h4>
          {summary && (
            <p className="font-overline text-[0.6rem] text-stone-500 mt-1">{summary}</p>
          )}
        </div>
        <ChevronDown
          size={20}
          className={`text-stone-400 transition-transform ${open ? "rotate-180 text-[#DE9E48]" : ""}`}
        />
      </button>
      {open && <div className="p-5 border-t border-white/10">{children}</div>}
    </section>
  );
}

function Field({ label, value, onChange, placeholder = "" }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field-input"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="field-textarea"
      />
    </div>
  );
}

function HeadingFields({ section, data, update }) {
  return (
    <div className="space-y-5">
      <Field
        label="Overline (small label)"
        value={data.overline}
        onChange={(v) => update(section, { overline: v })}
      />
      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Headline — first part"
          value={data.title_top}
          onChange={(v) => update(section, { title_top: v })}
        />
        <Field
          label="Headline — italic accent"
          value={data.title_italic}
          onChange={(v) => update(section, { title_italic: v })}
        />
      </div>
      <TextArea
        label="Subtitle / description"
        value={data.subtitle}
        onChange={(v) => update(section, { subtitle: v })}
        rows={2}
      />
    </div>
  );
}
